import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { KeyRound } from 'lucide-react';
import adminApi from '../api/adminApi';
import { useOnce } from '../hooks/useOnce';

// Settings -> Security -> Forgot Password. Saved as soon as it is toggled;
// the backend enforces it on every forgot-password endpoint.
const ForgotPasswordSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState(null);

  const fetchSetting = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await adminApi.getForgotPasswordSetting();
      setData(response.data.data);
    } catch (error) {
      setLoadError(error.response?.data?.message || 'Failed to load the forgot password setting');
    } finally {
      setLoading(false);
    }
  };

  useOnce(() => {
    fetchSetting();
  }, []);

  const toggle = async () => {
    if (!data || saving) return;
    setSaving(true);
    try {
      const response = await adminApi.updateForgotPasswordSetting(!data.enabled);
      setData(response.data.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save the forgot password setting');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    border: '1px solid #e5e7eb',
    padding: '2rem',
    maxWidth: '640px',
    marginTop: '1.5rem'
  };

  if (loading) {
    return <div style={cardStyle}><p style={{ color: '#6b7280', margin: 0 }}>Loading forgot password setting...</p></div>;
  }

  if (loadError) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{loadError}</p>
        <button onClick={fetchSetting} style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  const { enabled } = data;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <KeyRound size={22} color="#1d4ed8" />
          </div>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', margin: '0 0 0.25rem' }}>Forgot Password</h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              Shows "Forgot Password?" on the login page so users can reset their password with a 6-digit code emailed to their registered address.
              When off, the link is hidden and password resets are refused.
            </p>
          </div>
        </div>
        <span style={{
          padding: '0.25rem 0.625rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          backgroundColor: enabled ? '#dcfce7' : '#f3f4f6',
          color: enabled ? '#15803d' : '#6b7280'
        }}>
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
        <label htmlFor="forgot-password-toggle" style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
          Enable Forgot Password
        </label>
        <button
          id="forgot-password-toggle"
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={saving}
          onClick={toggle}
          style={{
            position: 'relative',
            width: '44px',
            height: '24px',
            borderRadius: '9999px',
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
            backgroundColor: enabled ? '#3b82f6' : '#d1d5db',
            opacity: saving ? 0.7 : 1,
            transition: 'background-color 0.15s ease',
            flexShrink: 0
          }}
        >
          <span style={{
            position: 'absolute',
            top: '2px',
            left: enabled ? '22px' : '2px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            backgroundColor: 'white',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'left 0.15s ease'
          }} />
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {enabled ? 'On' : 'Off'}
          </span>
        </button>
      </div>

      {data.updated_at && (
        <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '1rem 0 0' }}>
          Last updated: {new Date(String(data.updated_at).replace(' ', 'T')).toLocaleString()}
          {data.updated_by ? ` by ${data.updated_by}` : ''}
        </p>
      )}
    </div>
  );
};

export default ForgotPasswordSettings;
